import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Icon from '../components/ui/icon';
import AssistantHeader from '../components/layout/assistant-header';
import { useAuth } from '../contexts/AuthContext';
import { agentFlowChatService } from '../services/agentFlowChatService';
import { Message, PaginationInfo } from '../types/chat';
import { CHAT_CONFIG, QUICK_ACTIONS } from '../utils/chatConstants';
import { playDingSound } from '../utils/sound';
import { useVoiceRecognition } from '../hooks/useVoiceRecognition';
import { VoiceRecordingModal } from '../components/ui/voice-recording-modal';
import { ChatWelcomeScreen } from '../components/ui/chat-welcome-screen';
import { ChatMessageList } from '../components/ui/chat-message-list';
import { ChatInput } from '../components/ui/chat-input';

// Speech Recognition API types
declare global {
    interface Window {
        SpeechRecognition: any;
        webkitSpeechRecognition: any;
    }
}


type QuickAction = typeof QUICK_ACTIONS[number];

const Assistant: React.FC = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [showQuickActions, setShowQuickActions] = useState(false);
    const [showImageOptions, setShowImageOptions] = useState(false);
    const [sessionId, setSessionId] = useState<string>('');
    const [pagination, setPagination] = useState<PaginationInfo>({
        hasMore: true,
        totalCount: 0,
        currentPage: 0,
        pageSize: CHAT_CONFIG.PAGE_SIZE
    });
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    // Removed isSyncing state - no sync needed for MVP
    const [error, setError] = useState<string | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isUserActive, setIsUserActive] = useState(true);
    const [lastUserActivity, setLastUserActivity] = useState(Date.now());
    const [showVoiceDrawer, setShowVoiceDrawer] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    // Initialize hooks
    // const { showCompleteResponse } = useTypingAnimation(setMessages, setIsTyping);

    // Track user activity
    const updateUserActivity = useCallback(() => {
        setIsUserActive(true);
        setLastUserActivity(Date.now());
    }, []);

    // Check if user is inactive (no activity for 30 seconds)
    const checkUserInactivity = useCallback(() => {
        const now = Date.now();
        const timeSinceLastActivity = now - lastUserActivity;
        const inactiveThreshold = 30 * 1000; // 30 seconds

        if (timeSinceLastActivity > inactiveThreshold) {
            setIsUserActive(false);
        }
    }, [lastUserActivity]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Load chat history with pagination
    const loadChatHistory = useCallback(async (page: number = 0, append: boolean = false) => {
        if (!user?.outlet_id || !sessionId || isLoadingHistory || isRetrying || isTyping) return;

        setIsLoadingHistory(true);
        setError(null);

        try {
            const offset = page * pagination.pageSize;
            // Use fast version to get data immediately
            const historyMessages = await agentFlowChatService.getChatHistoryFast(
                user.outlet_id,
                sessionId,
                pagination.pageSize,
                offset
            );

            const convertedMessages: Message[] = historyMessages.map(msg => ({
                id: msg.id,
                type: msg.role,
                content: msg.content,
                timestamp: new Date(msg.timestamp),
                isTyping: false
            }));

            if (append) {
                setMessages(prev => [...convertedMessages, ...prev]);
            } else {
                setMessages(convertedMessages);
            }

            const totalCount = await agentFlowChatService.getMessageCount(user.outlet_id, sessionId);
            setPagination(prev => ({
                ...prev,
                hasMore: offset + pagination.pageSize < totalCount,
                totalCount,
                currentPage: page
            }));

            // If loading more (append), don't scroll to bottom
            if (!append) {
                // Scroll will be handled by useEffect
            }

        } catch (error) {
            setError(error instanceof Error ? error.message : 'Không thể tải lịch sử chat');
        } finally {
            setIsLoadingHistory(false);
        }
    }, [user?.outlet_id, sessionId, pagination.pageSize, isLoadingHistory, isRetrying, isTyping]);

    // Initialize session and load recent messages
    useEffect(() => {
        const initializeChat = async () => {
            if (!user?.outlet_id) return;

            try {
                setError(null);
                setIsLoadingHistory(true);
                updateUserActivity(); // Mark user as active

                // Get or create session with persistence
                const newSessionId = await agentFlowChatService.getOrCreateSession(user.outlet_id);
                setSessionId(newSessionId);

                // Load messages from localStorage first, then API as fallback
                const storageKey = `agentflow_chat_${user.outlet_id}_${newSessionId}`;
                const storedMessages = localStorage.getItem(storageKey);

                if (storedMessages) {
                    try {
                        const parsedMessages = JSON.parse(storedMessages);

                        // Convert timestamp strings back to Date objects and validate content
                        const messagesWithDates = parsedMessages.map((msg: any) => {
                            const processedMsg = {
                                ...msg,
                                // Convert role to type for compatibility
                                type: msg.role || msg.type,
                                timestamp: new Date(msg.timestamp),
                                // Ensure content is properly formatted
                                content: typeof msg.content === 'string' ? msg.content : String(msg.content || '')
                            };


                            return processedMsg;
                        });

                        setMessages(messagesWithDates);
                        setIsLoadingHistory(false);

                        // Set pagination info for local storage
                        setPagination(prev => ({
                            ...prev,
                            hasMore: false, // No more messages from local storage
                            totalCount: messagesWithDates.length,
                            currentPage: 0
                        }));
                        return; // Use local storage data, don't fallback to API
                    } catch (error) {
                        // Clear corrupted data
                        localStorage.removeItem(storageKey);
                    }
                } else {
                    // Try to find messages from any session for this outlet
                    const allKeys = Object.keys(localStorage);
                    const outletKeys = allKeys.filter(key => key.startsWith(`agentflow_chat_${user.outlet_id}_`));

                    if (outletKeys.length > 0) {
                        // Clean up old sessions - keep only the most recent one
                        if (outletKeys.length > 1) {
                            // Sort by session ID to get the most recent
                            outletKeys.sort();
                            const latestKey = outletKeys[outletKeys.length - 1];

                            // Remove old sessions
                            outletKeys.slice(0, -1).forEach(oldKey => {
                                localStorage.removeItem(oldKey);
                            });

                            // Update session ID to the latest one
                            const latestSessionId = latestKey.split('_').pop();
                            if (latestSessionId) {
                                localStorage.setItem(`agentflow_session_${user.outlet_id}`, latestSessionId);
                            }
                        }

                        // Use the most recent session
                        const latestKey = outletKeys[outletKeys.length - 1];
                        const fallbackMessages = localStorage.getItem(latestKey);

                        if (fallbackMessages) {
                            try {
                                const parsedMessages = JSON.parse(fallbackMessages);
                                const messagesWithDates = parsedMessages.map((msg: any) => ({
                                    ...msg,
                                    type: msg.role || msg.type,
                                    timestamp: new Date(msg.timestamp),
                                    content: typeof msg.content === 'string' ? msg.content : String(msg.content || '')
                                }));

                                // Set messages from fallback session
                                setMessages(messagesWithDates);
                                setIsLoadingHistory(false);

                                setPagination(prev => ({
                                    ...prev,
                                    hasMore: false,
                                    totalCount: messagesWithDates.length
                                }));

                                return;
                            } catch (error) {
                                // Ignore parsing errors
                            }
                        }
                    }
                }

                // Fallback to API if no local storage
                try {
                    const historyMessages = await agentFlowChatService.getChatHistoryFast(
                        user.outlet_id,
                        newSessionId,
                        pagination.pageSize,
                        0
                    );

                    const convertedMessages: Message[] = historyMessages.map(msg => ({
                        id: msg.id,
                        type: msg.role,
                        content: msg.content,
                        timestamp: new Date(msg.timestamp),
                        isTyping: false
                    }));

                    setMessages(convertedMessages);

                    const totalCount = await agentFlowChatService.getMessageCount(user.outlet_id, newSessionId);
                    setPagination(prev => ({
                        ...prev,
                        hasMore: pagination.pageSize < totalCount,
                        totalCount,
                        currentPage: 0
                    }));

                } catch (error) {
                    setError('Không thể tải lịch sử chat. Sẽ sử dụng chế độ offline.');
                } finally {
                    setIsLoadingHistory(false);
                }
            } catch (error) {
                setError('Không thể khởi tạo chat. Vui lòng thử lại.');
            }
        };

        initializeChat();
    }, [user?.outlet_id, pagination.pageSize, updateUserActivity, isUserActive]);

    // Check user inactivity and sync when inactive
    useEffect(() => {
        const inactivityInterval = setInterval(() => {
            checkUserInactivity();
        }, 5000); // Check every 5 seconds

        return () => clearInterval(inactivityInterval);
    }, [checkUserInactivity, isUserActive]);

    // No automatic sync needed - using local storage only for MVP

    // Track user activity events
    useEffect(() => {
        const handleUserActivity = () => {
            updateUserActivity();
        };

        // Add event listeners for user activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        events.forEach(event => {
            document.addEventListener(event, handleUserActivity, true);
        });

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleUserActivity, true);
            });
        };
    }, [updateUserActivity]);

    // Simple auto scroll to bottom when messages change
    useEffect(() => {
        if (messages.length > 0) {
            // Small delay to ensure DOM is updated
            setTimeout(() => {
                scrollToBottom();
            }, 100);
        }
    }, [messages.length]);

    // Load more messages
    const loadMoreMessages = useCallback(() => {
        if (pagination.hasMore && !isLoadingHistory && !isRetrying) {
            // Store current scroll position
            const container = messagesContainerRef.current;
            const scrollHeight = container?.scrollHeight || 0;
            const scrollTop = container?.scrollTop || 0;

            loadChatHistory(pagination.currentPage + 1, true);

            // After loading, maintain scroll position
            setTimeout(() => {
                if (container) {
                    const newScrollHeight = container.scrollHeight;
                    const heightDifference = newScrollHeight - scrollHeight;
                    container.scrollTop = scrollTop + heightDifference;
                }
            }, CHAT_CONFIG.SCROLL_MAINTAIN_DELAY);
        }
    }, [pagination.hasMore, pagination.currentPage, isLoadingHistory, isRetrying, loadChatHistory]);

    // Retry mechanism
    const retryOperation = useCallback(async () => {
        if (isRetrying) return;

        setIsRetrying(true);
        setError(null);

        try {
            if (sessionId) {
                await loadChatHistory(0, false);
            } else if (user?.outlet_id) {
                // Use getOrCreateSession for better session management
                const newSessionId = await agentFlowChatService.getOrCreateSession(user.outlet_id);
                setSessionId(newSessionId);
                await loadChatHistory(0, false);
            }
        } catch (error) {
            setError('Không thể kết nối. Vui lòng kiểm tra mạng và thử lại.');
        } finally {
            setIsRetrying(false);
        }
    }, [isRetrying, sessionId, user?.outlet_id, loadChatHistory]);

    // Clear all messages
    const clearAllMessages = useCallback(async () => {
        if (!user?.outlet_id) return;

        try {
            await agentFlowChatService.deleteAllMessages(user.outlet_id);
            setMessages([]);
            setPagination({
                hasMore: false,
                totalCount: 0,
                currentPage: 0,
                pageSize: 20
            });
            // Scroll will be handled by useEffect
        } catch (error) {
            // Silent fail for clearing messages
        }
    }, [user?.outlet_id]);

    // Clear old messages with context on component mount (only once)
    useEffect(() => {
        if (user?.outlet_id && sessionId) {
            const storageKey = `agentflow_chat_${user.outlet_id}_${sessionId}`;
            const existingMessages = localStorage.getItem(storageKey);
            if (existingMessages) {
                try {
                    const messages = JSON.parse(existingMessages);
                    // Filter out any messages that contain context (but don't clear everything)
                    const cleanMessages = messages.filter((msg: any) =>
                        !msg.content || !msg.content.includes('[Context:')
                    );

                    // Only update if we actually filtered something out
                    if (cleanMessages.length !== messages.length) {
                        localStorage.setItem(storageKey, JSON.stringify(cleanMessages));
                        // Update state if we're currently showing messages
                        if (messages.length > 0) {
                            setMessages(cleanMessages.map((msg: any) => ({
                                ...msg,
                                timestamp: new Date(msg.timestamp)
                            })));
                        }
                    }
                } catch (error) {
                    // If parsing fails, clear the storage
                    localStorage.removeItem(storageKey);
                }
            }
        }
    }, [user?.outlet_id, sessionId]); // Include sessionId dependency

    const handleSendMessage = useCallback(async (content?: string) => {
        const messageContent = content || message.trim();
        if (!messageContent || !user?.outlet_id || !sessionId) return;

        updateUserActivity(); // Mark user as active when sending message

        // Add user message immediately for better UX
        const userMessage: Message = {
            id: `user_${Date.now()}`,
            type: 'user',
            content: messageContent,
            timestamp: new Date()
        };

        // Add user message to chat immediately
        setMessages(prev => [...prev, userMessage]);
        setMessage('');

        // Scroll to bottom immediately after adding user message
        setTimeout(() => {
            scrollToBottom();
        }, 50);

        // Add typing indicator immediately after user message
        const assistantTypingMessage: Message = {
            id: `assistant_typing_${Date.now()}`,
            type: 'assistant',
            content: '',
            timestamp: new Date(),
            isTyping: true
        };

        // Add typing indicator immediately
        setMessages(prev => [...prev, assistantTypingMessage]);
        setIsTyping(true);

        // Scroll to bottom after adding typing indicator
        setTimeout(() => {
            scrollToBottom();
        }, 50);

        // Store typing message ID for later removal
        const typingMessageId = assistantTypingMessage.id;

        // Send message to AgentFlow and get response
        try {
            // Send to AgentFlow API with outlet info
            const result = await agentFlowChatService.sendMessageToAPI(
                user.outlet_id,
                sessionId,
                messageContent, // Clean message for UI
                user // Pass full user object as outlet info
            );


            // Remove typing indicator and add final response
            setMessages(prev => {
                // Remove the typing message and add the final response
                const withoutTyping = prev.filter(msg => msg.id !== typingMessageId);
                const finalAssistantMessage: Message = {
                    id: `assistant_${Date.now()}`,
                    type: 'assistant',
                    content: result.response,
                    timestamp: new Date(),
                    isTyping: false
                };
                return [...withoutTyping, finalAssistantMessage];
            });

            // Play ding sound
            playDingSound();

            // Clear typing state
            setIsTyping(false);

            // Scroll to bottom after receiving response
            setTimeout(() => {
                scrollToBottom();
            }, 50);

        } catch (error) {
            // Show friendly error message instead of random responses
            const errorMessage = 'Xin lỗi, tôi đang gặp sự cố kỹ thuật. Vui lòng thử lại sau ít phút nhé! 😊';

            // Remove typing indicator and add error message
            setMessages(prev => {
                const withoutTyping = prev.filter(msg => msg.id !== typingMessageId);
                const errorAssistantMessage: Message = {
                    id: `assistant_error_${Date.now()}`,
                    type: 'assistant',
                    content: errorMessage,
                    timestamp: new Date(),
                    isTyping: false
                };
                return [...withoutTyping, errorAssistantMessage];
            });

            // Play ding sound
            playDingSound();

            // Clear typing state
            setIsTyping(false);

            // Scroll to bottom after error response
            setTimeout(() => {
                scrollToBottom();
            }, 50);

            // Save fallback response (user message already saved by service)
            try {
                await agentFlowChatService.saveMessage(user.outlet_id, sessionId, 'assistant', errorMessage);
            } catch (saveError) {
                // Silent fail for local storage errors
            }
        }
    }, [message, user, sessionId, updateUserActivity]);


    // Initialize voice recognition hook
    const { isRecording, startRecording, stopRecording, isRequestingPermission, currentTranscript, interimTranscript, resetTranscript } = useVoiceRecognition(
        (transcript: string) => {
            // Chỉ điền text vào ô input, KHÔNG tự động đóng modal
            // User sẽ quyết định gửi hay không thông qua nút "Gửi yêu cầu"
            setMessage(transcript);
        },
        (error: string) => {
            setError(error);
            // Reset transcript khi có lỗi để có thể thử lại
            resetTranscript();
        }
    );

    // Auto-start recording when permission is granted (only once)
    const [hasAutoStarted, setHasAutoStarted] = useState(false);
    
    useEffect(() => {
        if (showVoiceDrawer && !isRequestingPermission && !isRecording && !currentTranscript && !hasAutoStarted) {
            // Tự động bắt đầu ghi âm sau khi có quyền (chỉ 1 lần)
            setHasAutoStarted(true);
            const timeoutId = setTimeout(() => {
                if (!isRecording) { // Double check before starting
                    startRecording();
                }
            }, 200);
            
            return () => clearTimeout(timeoutId);
        }
    }, [showVoiceDrawer, isRequestingPermission, isRecording, currentTranscript, hasAutoStarted, startRecording]);

    // Reset auto-start flag when modal closes
    useEffect(() => {
        if (!showVoiceDrawer) {
            setHasAutoStarted(false);
        }
    }, [showVoiceDrawer]);

    // Handle URL parameters and navigation state for auto-filling input
    useEffect(() => {
        const action = searchParams.get('action');
        const messageParam = searchParams.get('message');

        // Check for navigation state (from edit order action)
        const navigationState = location.state as { initialMessage?: string } | null;

        if (navigationState?.initialMessage && messages.length === 0) {
            // Auto-fill the input with the message from navigation state
            setMessage(navigationState.initialMessage);
            // Focus on textarea after a short delay
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                }
            }, 100);
        } else if (action && messageParam && messages.length === 0) {
            // Auto-fill the input with the message from URL parameters
            setMessage(messageParam);
            // Focus on textarea after a short delay
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                }
            }, 100);
        }
    }, [searchParams, location.state, messages.length]);

    const handleQuickAction = (action: QuickAction) => {
        setShowQuickActions(false);
        handleSendMessage(action.action);
    };

    const handleVoiceRecord = () => {
        setShowVoiceDrawer(true);
        setError(null); // Clear any previous errors
        resetTranscript(); // Reset transcript before opening
        // Tự động bắt đầu ghi âm khi mở drawer
        setTimeout(() => {
            startRecording();
        }, 100);
    };

    const handleVoiceRestart = () => {
        setError(null); // Clear any previous errors
        resetTranscript(); // Reset transcript before starting
        startRecording();
    };

    const handleVoiceStop = () => {
        // Set a small delay to show stopping state before actually stopping
        setTimeout(() => {
            stopRecording(false); // Dừng recording nhưng KHÔNG gửi transcript tự động
            // Chuyển transcript vào message input và đóng modal
            const finalTranscript = (currentTranscript + interimTranscript).trim();
            if (finalTranscript) {
                setMessage(finalTranscript);
            }
            setShowVoiceDrawer(false);
            resetTranscript();
        }, 50);
    };

    const handleVoiceCancel = () => {
        // Stop recording if it's running but don't send transcript
        if (isRecording) {
            stopRecording(false); // Không gửi transcript khi cancel
        }
        setShowVoiceDrawer(false);
        setMessage(''); // Clear any existing message
        setError(null);
        resetTranscript();
    };

    const handleVoiceSendMessage = () => {
        const finalTranscript = (currentTranscript + interimTranscript).trim();
        if (finalTranscript) {
            setShowVoiceDrawer(false);
            // Send message directly without filling input
            handleSendMessage(finalTranscript);
            resetTranscript();
        }
    };

    const handleImageUpload = () => {
        // TODO: Implement image upload
    };


    const adjustTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
        }
    };


    useEffect(() => {
        adjustTextareaHeight();
    }, [message]);

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showQuickActions || showImageOptions) {
                const target = event.target as HTMLElement;
                if (!target.closest('.context-menu-container')) {
                    setShowQuickActions(false);
                    setShowImageOptions(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showQuickActions, showImageOptions]);


    return (
        <div className="h-screen bg-background flex flex-col">
            {/* 1. Header - Fixed Top */}
            <div className="bg-background">
                <AssistantHeader onClearAllMessages={clearAllMessages} />
            </div>

            {/* Sync Indicator - Removed for MVP */}

            {/* 2. Content - Scrollable Area */}

            <div className="h-full overflow-y-auto">
                <div className="max-w-4xl mx-auto px-4 py-4 bg-background">
                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mx-4 mt-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Icon name="alert-circle" size={20} className="text-red-500 mr-2" />
                                    <span className="text-red-700 text-sm">{error}</span>
                                </div>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={retryOperation}
                                    disabled={isRetrying}
                                >
                                    {isRetrying ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin mr-2" />
                                            Đang thử...
                                        </>
                                    ) : (
                                        'Thử lại'
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Messages Area */}
                    <div className="space-y-4" ref={messagesContainerRef}>
                        {messages.length > 0 && (
                            <div className="w-full space-y-4">
                                {/* Load More Button */}
                                {pagination.hasMore && (
                                    <div className="flex justify-center py-4">
                                        <Button
                                            variant="outline"
                                            onClick={loadMoreMessages}
                                            disabled={isLoadingHistory}
                                            className="flex items-center gap-2"
                                        >
                                            {isLoadingHistory ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                                    Đang tải...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon name="chevron-up" size={16} />
                                                    Xem tin nhắn cũ ({pagination.totalCount - messages.length} tin còn lại)
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}

                                <ChatMessageList
                                    key={`messages-${messages.length}-${sessionId}`}
                                    messages={messages}
                                    userName={user?.contact_person || 'Bạn'}
                                    assistantName="Trợ lý AI Ngọc"
                                />
                            </div>
                        )}

                        {/* Welcome Screen - Only show when no messages */}
                        {messages.length === 0 && (
                            <ChatWelcomeScreen
                                userName={user?.contact_person || 'Bạn'}
                                outletName={user?.name || 'Tạp hóa Pango'}
                                onVoiceRecord={handleVoiceRecord}
                                onQuickAction={handleQuickAction}
                                quickActions={QUICK_ACTIONS}
                            />
                        )}

                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>

            {/* 3. Footer - Fixed Bottom Input Area */}
            <div className="bg-background">
                <div className="max-w-4xl mx-auto px-4 py-4">

                    {/* Chat Input Component */}
                    <ChatInput
                        ref={textareaRef}
                        message={message}
                        isTyping={isTyping}
                        showQuickActions={showQuickActions}
                        showImageOptions={showImageOptions}
                        onMessageChange={setMessage}
                        onSendMessage={handleSendMessage}
                        onVoiceRecord={handleVoiceRecord}
                        onImageUpload={handleImageUpload}
                        onQuickAction={handleQuickAction}
                        onToggleQuickActions={() => setShowQuickActions(!showQuickActions)}
                        onToggleImageOptions={() => setShowImageOptions(!showImageOptions)}
                        onCloseMenus={() => {
                            setShowQuickActions(false);
                            setShowImageOptions(false);
                        }}
                        quickActions={QUICK_ACTIONS}
                    />
                </div>
            </div>

            {/* Voice Recording Modal */}
            <VoiceRecordingModal
                isOpen={showVoiceDrawer}
                onClose={() => {
                    // Cleanup when closing modal - don't send transcript
                    if (isRecording) {
                        stopRecording(false); // Không gửi transcript khi đóng modal
                    }
                    setShowVoiceDrawer(false);
                    setError(null);
                    resetTranscript();
                }}
                isRecording={isRecording}
                isRequestingPermission={isRequestingPermission}
                currentTranscript={currentTranscript}
                interimTranscript={interimTranscript}
                onStartRecording={handleVoiceRestart}
                onStopRecording={handleVoiceStop}
                onCancel={handleVoiceCancel}
                onSendMessage={handleVoiceSendMessage}
                userName={user?.contact_person || 'Bạn'}
                error={error}
            />
        </div>
    );
};

export default Assistant;